import React, { useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Group,
  NumberInput,
  Paper,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconArrowLeft,
  IconChecklist,
  IconClipboardList,
  IconInfoCircle,
  IconPackage,
  IconPackageOff,
  IconReceipt2,
  IconRotate,
  IconSearch,
  IconShieldCheck,
  IconTruckDelivery,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';

const selectedQuotesSeed = [
  {
    id: 'Q-2026-0041',
    requisitionId: 'PR-2026-0105',
    requisitionTitle: 'Yeni personel için çalışma ekipmanı talebi',
    vendorName: 'OfisDonanım Ltd.',
    department: 'Bilgi İşlem',
    requester: 'Muhammed Selvi',
    status: 'READY_FOR_RECEIPT',
    deliveryTargetDate: '2026-03-25',
    paymentTerms: '15 gün vade',
    selectedAt: '2026-03-19 14:20',
    selectedBy: 'Ahmet Demir',
    totalAmount: 98500,
    lines: [
      {
        id: 'QL-6',
        lineNo: 1,
        description: 'Dell Latitude 15 Laptop',
        orderedQty: 1,
        uom: 'Adet',
        unitPrice: 84000,
      },
      {
        id: 'QL-7',
        lineNo: 2,
        description: 'Laptop Çantası',
        orderedQty: 1,
        uom: 'Adet',
        unitPrice: 3500,
      },
      {
        id: 'QL-8',
        lineNo: 3,
        description: 'Kablosuz Mouse',
        orderedQty: 1,
        uom: 'Adet',
        unitPrice: 1100,
      },
      {
        id: 'QL-9',
        lineNo: 4,
        description: 'Kurulum ve teslimat',
        orderedQty: 1,
        uom: 'Hizmet',
        unitPrice: 9900,
      },
    ],
  },
  {
    id: 'Q-2026-0050',
    requisitionId: 'PR-2026-0108',
    requisitionTitle: 'Aylık kırtasiye ve sarf malzeme alımı',
    vendorName: 'OfisDonanım Ltd.',
    department: 'İdari İşler',
    requester: 'Ayşe Kaya',
    status: 'PARTIAL_RECEIPT',
    deliveryTargetDate: '2026-03-23',
    paymentTerms: '30 gün vade',
    selectedAt: '2026-03-19 09:10',
    selectedBy: 'Can Yılmaz',
    totalAmount: 12480,
    lines: [
      {
        id: 'QL-10',
        lineNo: 1,
        description: 'A4 Fotokopi Kağıdı',
        orderedQty: 20,
        uom: 'Koli',
        unitPrice: 440,
      },
      {
        id: 'QL-11',
        lineNo: 2,
        description: 'Tükenmez Kalem',
        orderedQty: 50,
        uom: 'Adet',
        unitPrice: 18,
      },
    ],
  },
];

const conditionOptions = [
  { value: 'OK', label: 'Sorunsuz' },
  { value: 'DAMAGED', label: 'Hasarlı' },
  { value: 'PARTIAL', label: 'Eksik Teslim' },
  { value: 'WRONG_ITEM', label: 'Yanlış Ürün' },
];

const currencyFormatter = new Intl.NumberFormat('tr-TR', {
  style: 'currency',
  currency: 'TRY',
  maximumFractionDigits: 2,
});

const statusConfig = {
  READY_FOR_RECEIPT: { label: 'Teslim Bekleniyor', color: 'blue' },
  PARTIAL_RECEIPT: { label: 'Kısmi Teslim', color: 'orange' },
  RECEIVED: { label: 'Teslim Alındı', color: 'teal' },
  DRAFT: { label: 'Taslak', color: 'gray' },
  CONFIRMED: { label: 'Kesinleşti', color: 'teal' },
  OK: { label: 'Sorunsuz', color: 'teal' },
  DAMAGED: { label: 'Hasarlı', color: 'red' },
  PARTIAL: { label: 'Eksik Teslim', color: 'orange' },
  WRONG_ITEM: { label: 'Yanlış Ürün', color: 'violet' },
};

function StatusBadge({ status }) {
  const cfg = statusConfig[status] || { label: status, color: 'gray' };
  return (
    <Badge variant="light" color={cfg.color} radius="sm">
      {cfg.label}
    </Badge>
  );
}

function SummaryCard({ title, value, subtitle, color, icon: Icon }) {
  return (
    <Card withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Text size="sm" c="dimmed" fw={500}>{title}</Text>
          <Text fw={800} size="1.55rem" lh={1.1}>{value}</Text>
          <Text size="xs" c="dimmed">{subtitle}</Text>
        </Stack>
        <ThemeIcon color={color} variant="light" size={46} radius="md">
          <Icon size={22} />
        </ThemeIcon>
      </Group>
    </Card>
  );
}

function SectionCard({ title, description, children, action }) {
  return (
    <Paper withBorder radius="lg" p="lg" shadow="sm">
      <Group justify="space-between" align="flex-start" mb="md">
        <div>
          <Title order={4}>{title}</Title>
          {description ? <Text size="sm" c="dimmed" mt={4}>{description}</Text> : null}
        </div>
        {action}
      </Group>
      {children}
    </Paper>
  );
}

function InfoRow({ label, value }) {
  return (
    <>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Text size="sm" c="dimmed">{label}</Text>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Text size="sm" fw={600} ta="right">{value || '-'}</Text>
        ) : (
          value
        )}
      </Group>
      <Divider />
    </>
  );
}

export default function ProcurementGoodsReceiptPage() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [selectedQuoteId, setSelectedQuoteId] = useState(selectedQuotesSeed[0]?.id || '');
  const [receiptHeader, setReceiptHeader] = useState({
    goodsReceiptNo: 'GRN-2026-0021',
    deliveryNoteNo: 'IRS-45871',
    receivedBy: 'Depo Sorumlusu',
    receivedAt: '2026-03-20T10:30',
    status: 'DRAFT',
    note: 'Laptop kutusunda darbe izi gözlemlendi, kontrol açıldı.',
  });

  const [receiptLines, setReceiptLines] = useState([
    {
      quoteLineId: 'QL-6',
      lineNo: 1,
      description: 'Dell Latitude 15 Laptop',
      orderedQty: 1,
      receivedQty: 1,
      acceptedQty: 0,
      rejectedQty: 1,
      uom: 'Adet',
      conditionStatus: 'DAMAGED',
      note: 'Kasa kenarında ezik mevcut.',
    },
    {
      quoteLineId: 'QL-7',
      lineNo: 2,
      description: 'Laptop Çantası',
      orderedQty: 1,
      receivedQty: 1,
      acceptedQty: 1,
      rejectedQty: 0,
      uom: 'Adet',
      conditionStatus: 'OK',
      note: '',
    },
    {
      quoteLineId: 'QL-8',
      lineNo: 3,
      description: 'Kablosuz Mouse',
      orderedQty: 1,
      receivedQty: 0,
      acceptedQty: 0,
      rejectedQty: 0,
      uom: 'Adet',
      conditionStatus: 'PARTIAL',
      note: 'Sevkiyatta eksik çıktı.',
    },
    {
      quoteLineId: 'QL-9',
      lineNo: 4,
      description: 'Kurulum ve teslimat',
      orderedQty: 1,
      receivedQty: 1,
      acceptedQty: 1,
      rejectedQty: 0,
      uom: 'Hizmet',
      conditionStatus: 'OK',
      note: '',
    },
  ]);

  const filteredQuotes = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return selectedQuotesSeed;
    return selectedQuotesSeed.filter((item) => JSON.stringify(item).toLowerCase().includes(q));
  }, [search]);

  const selectedQuote = useMemo(() => {
    return selectedQuotesSeed.find((item) => item.id === selectedQuoteId) || selectedQuotesSeed[0] || null;
  }, [selectedQuoteId]);

  const stats = useMemo(() => {
    return {
      awaiting: selectedQuotesSeed.filter((item) => item.status === 'READY_FOR_RECEIPT').length,
      partial: selectedQuotesSeed.filter((item) => item.status === 'PARTIAL_RECEIPT').length,
      volume: selectedQuotesSeed.reduce((acc, item) => acc + item.totalAmount, 0),
      receiptCandidates: selectedQuotesSeed.length,
    };
  }, []);

  const totals = useMemo(() => {
    const ordered = receiptLines.reduce((acc, line) => acc + Number(line.orderedQty || 0), 0);
    const received = receiptLines.reduce((acc, line) => acc + Number(line.receivedQty || 0), 0);
    const accepted = receiptLines.reduce((acc, line) => acc + Number(line.acceptedQty || 0), 0);
    const rejected = receiptLines.reduce((acc, line) => acc + Number(line.rejectedQty || 0), 0);
    const discrepancies = receiptLines.filter(
      (line) => line.conditionStatus !== 'OK' || Number(line.orderedQty) !== Number(line.receivedQty)
    ).length;

    return { ordered, received, accepted, rejected, discrepancies };
  }, [receiptLines]);

  const updateHeader = (field, value) => {
    setReceiptHeader((prev) => ({ ...prev, [field]: value }));
  };

  const updateLine = (lineNo, field, value) => {
    setReceiptLines((prev) =>
      prev.map((line) => {
        if (line.lineNo !== lineNo) return line;
        const updated = { ...line, [field]: value };
        return updated;
      })
    );
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <Paper
          withBorder
          radius="xl"
          p="xl"
          shadow="sm"
          style={{
            background: 'linear-gradient(135deg, rgba(245,247,250,1) 0%, rgba(255,255,255,1) 100%)',
          }}
        >
          <Group justify="space-between" align="flex-start" gap="xl">
            <Stack gap="sm" maw={860}>
              <Group gap="sm">
                <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} px={0} onClick={() => navigate("/purchase-dashboard")}>
                  Geri Dön
                </Button>
                <Badge variant="light" color="cyan" leftSection={<IconPackage size={14} />}>
                  Statik demo mal kabul ekranı
                </Badge>
              </Group>
              <div>
                <Title order={1}>Mal Kabul / Teslim Alma</Title>
                <Text size="sm" c="dimmed" mt="sm">
                  Seçilen teklif sonrası gelen ürünlerin teslim alındığı, eksik / hasarlı kontrolünün yapıldığı ve
                  kabul-red ayrımının işlendiği operasyon ekranı. Bu adım iade ve fatura sürecinin temelini oluşturur.
                </Text>
              </div>
            </Stack>
          </Group>

          <SimpleGrid cols={{ base: 1, sm: 2, xl: 4 }} spacing="md" mt="xl">
            <SummaryCard title="Teslim Bekleyen" value={stats.awaiting} subtitle="Henüz mal kabul açılmamış kayıt" color="blue" icon={IconTruckDelivery} />
            <SummaryCard title="Kısmi Teslim" value={stats.partial} subtitle="Eksik veya parçalı teslimat" color="orange" icon={IconAlertTriangle} />
            <SummaryCard title="Aday Kayıt" value={stats.receiptCandidates} subtitle="Mal kabul oluşturulabilir teklif" color="teal" icon={IconChecklist} />
            <SummaryCard title="Toplam Hacim" value={currencyFormatter.format(stats.volume)} subtitle="Teslimat hacmi" color="grape" icon={IconReceipt2} />
          </SimpleGrid>
        </Paper>

        <Grid gutter="lg">
          <Grid.Col span={{ base: 12, xl: 3.5 }}>
            <Stack gap="md">
              <SectionCard title="Teklif Havuzu" description="Teslim alınacak seçilmiş teklifi seçin.">
                <Stack gap="sm">
                  <TextInput
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder="Talep, teklif, tedarikçi ara..."
                    leftSection={<IconSearch size={16} />}
                  />

                  <Stack gap="xs">
                    {filteredQuotes.map((item) => (
                      <Paper
                        key={item.id}
                        withBorder
                        radius="md"
                        p="sm"
                        onClick={() => setSelectedQuoteId(item.id)}
                        style={{
                          cursor: 'pointer',
                          borderColor: selectedQuoteId === item.id ? 'var(--mantine-color-cyan-4)' : undefined,
                          backgroundColor: selectedQuoteId === item.id ? 'var(--mantine-color-cyan-0)' : undefined,
                        }}
                      >
                        <Group justify="space-between" align="flex-start" wrap="nowrap">
                          <div>
                            <Text fw={700} size="sm">{item.id}</Text>
                            <Text size="sm" mt={2}>{item.requisitionTitle}</Text>
                            <Text size="xs" c="dimmed" mt={4}>{item.vendorName} • {item.department}</Text>
                          </div>
                          <Stack gap={6} align="flex-end">
                            <StatusBadge status={item.status} />
                            <Text fw={700} size="sm">{currencyFormatter.format(item.totalAmount)}</Text>
                          </Stack>
                        </Group>
                      </Paper>
                    ))}
                  </Stack>
                </Stack>
              </SectionCard>

            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, xl: 8.5 }}>
            <Stack gap="lg">
              {selectedQuote && (
                <>
                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Seçilen Teklif Özeti" description="Mal kabulün bağlandığı teklif ve talep bilgileri.">
                      <Stack gap="sm">
                        <InfoRow label="Teklif No" value={selectedQuote.id} />
                        <InfoRow label="Talep No" value={selectedQuote.requisitionId} />
                        <InfoRow label="Talep Başlığı" value={selectedQuote.requisitionTitle} />
                        <InfoRow label="Tedarikçi" value={selectedQuote.vendorName} />
                        <InfoRow label="Departman" value={selectedQuote.department} />
                        <InfoRow label="Talep Eden" value={selectedQuote.requester} />
                        <InfoRow label="Seçim Tarihi" value={selectedQuote.selectedAt} />
                        <InfoRow label="Seçen" value={selectedQuote.selectedBy} />
                        <InfoRow label="Teslim Hedefi" value={selectedQuote.deliveryTargetDate} />
                        <InfoRow label="Ödeme Koşulu" value={selectedQuote.paymentTerms} />
                        <InfoRow label="Toplam" value={currencyFormatter.format(selectedQuote.totalAmount)} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Mal Kabul Başlık Bilgileri" description="Oluşturulacak goods receipt belgesinin üst alanları.">
                      <Grid gutter="md">
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="Mal Kabul No"
                            value={receiptHeader.goodsReceiptNo}
                            onChange={(event) => updateHeader('goodsReceiptNo', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="İrsaliye No"
                            value={receiptHeader.deliveryNoteNo}
                            onChange={(event) => updateHeader('deliveryNoteNo', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="Teslim Alan"
                            value={receiptHeader.receivedBy}
                            onChange={(event) => updateHeader('receivedBy', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <TextInput
                            label="Teslim Zamanı"
                            type="datetime-local"
                            value={receiptHeader.receivedAt}
                            onChange={(event) => updateHeader('receivedAt', event.currentTarget.value)}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ base: 12, md: 6 }}>
                          <Select
                            label="Durum"
                            data={[
                              { value: 'DRAFT', label: 'Taslak' },
                              { value: 'CONFIRMED', label: 'Kesinleşti' },
                            ]}
                            value={receiptHeader.status}
                            onChange={(value) => updateHeader('status', value || 'DRAFT')}
                          />
                        </Grid.Col>
                        <Grid.Col span={12}>
                          <Textarea
                            label="Genel Not"
                            minRows={4}
                            value={receiptHeader.note}
                            onChange={(event) => updateHeader('note', event.currentTarget.value)}
                          />
                        </Grid.Col>
                      </Grid>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard title="Mal Kabul Satırları" description="Her kalem için gelen, kabul edilen ve reddedilen miktarları işleyin.">
                    <Stack gap="md">
                      {receiptLines.map((line) => {
                        const missingQty = Math.max(Number(line.orderedQty || 0) - Number(line.receivedQty || 0), 0);
                        return (
                          <Paper key={`${line.quoteLineId}-${line.lineNo}`} withBorder radius="md" p="md">
                            <Stack gap="md">
                              <Group justify="space-between" align="center">
                                <Group gap="xs">
                                  <ThemeIcon color="blue" variant="light" radius="md">
                                    <IconClipboardList size={16} />
                                  </ThemeIcon>
                                  <div>
                                    <Text fw={700}>Satır {line.lineNo}</Text>
                                    <Text size="xs" c="dimmed">{line.description}</Text>
                                  </div>
                                </Group>
                                <StatusBadge status={line.conditionStatus} />
                              </Group>

                              <Grid gutter="md">
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                  <NumberInput label="Sipariş Miktarı" value={line.orderedQty} readOnly />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                  <NumberInput
                                    label="Gelen Miktar"
                                    value={line.receivedQty}
                                    min={0}
                                    onChange={(value) => updateLine(line.lineNo, 'receivedQty', Number(value) || 0)}
                                  />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                  <NumberInput
                                    label="Kabul Edilen"
                                    value={line.acceptedQty}
                                    min={0}
                                    onChange={(value) => updateLine(line.lineNo, 'acceptedQty', Number(value) || 0)}
                                  />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 3 }}>
                                  <NumberInput
                                    label="Reddedilen"
                                    value={line.rejectedQty}
                                    min={0}
                                    onChange={(value) => updateLine(line.lineNo, 'rejectedQty', Number(value) || 0)}
                                  />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                  <TextInput label="Birim" value={line.uom} readOnly />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                  <Select
                                    label="Durum"
                                    data={conditionOptions}
                                    value={line.conditionStatus}
                                    onChange={(value) => updateLine(line.lineNo, 'conditionStatus', value || 'OK')}
                                  />
                                </Grid.Col>
                                <Grid.Col span={{ base: 12, md: 4 }}>
                                  <Paper radius="md" p="sm" bg="gray.0" mt={25}>
                                    <Group justify="space-between">
                                      <Text size="sm" c="dimmed">Eksik Miktar</Text>
                                      <Text fw={700}>{missingQty} {line.uom}</Text>
                                    </Group>
                                  </Paper>
                                </Grid.Col>
                                <Grid.Col span={12}>
                                  <Textarea
                                    label="Satır Notu"
                                    minRows={2}
                                    value={line.note}
                                    onChange={(event) => updateLine(line.lineNo, 'note', event.currentTarget.value)}
                                  />
                                </Grid.Col>
                              </Grid>
                            </Stack>
                          </Paper>
                        );
                      })}
                    </Stack>
                  </SectionCard>

                  <SimpleGrid cols={{ base: 1, xl: 2 }} spacing="md">
                    <SectionCard title="Teslimat Özeti" description="Anlık mal kabul toplamları ve sapmalar.">
                      <Stack gap="sm">
                        <InfoRow label="Toplam Sipariş" value={totals.ordered} />
                        <InfoRow label="Toplam Gelen" value={totals.received} />
                        <InfoRow label="Toplam Kabul" value={totals.accepted} />
                        <InfoRow label="Toplam Red" value={totals.rejected} />
                        <InfoRow label="Sorunlu Satır" value={<Badge color={totals.discrepancies > 0 ? 'orange' : 'teal'} variant="light">{totals.discrepancies}</Badge>} />
                        <InfoRow label="Mal Kabul Durumu" value={<StatusBadge status={receiptHeader.status} />} />
                      </Stack>
                    </SectionCard>

                    <SectionCard title="Sorunlu Kalemler" description="İade veya düzeltme gerektirebilecek satırlar.">
                      <Stack gap="sm">
                        {receiptLines.filter((line) => line.conditionStatus !== 'OK' || line.orderedQty !== line.receivedQty).length === 0 ? (
                          <Text size="sm" c="dimmed">Sorunlu satır bulunmuyor.</Text>
                        ) : (
                          receiptLines
                            .filter((line) => line.conditionStatus !== 'OK' || line.orderedQty !== line.receivedQty)
                            .map((line) => (
                              <Paper key={`issue-${line.lineNo}`} withBorder radius="md" p="sm">
                                <Group justify="space-between" align="flex-start">
                                  <div>
                                    <Text fw={700} size="sm">{line.description}</Text>
                                    <Text size="xs" c="dimmed" mt={4}>{line.note || 'Detay notu yok.'}</Text>
                                  </div>
                                  <Stack gap={6} align="flex-end">
                                    <StatusBadge status={line.conditionStatus} />
                                    <Badge variant="light" color="gray">Satır {line.lineNo}</Badge>
                                  </Stack>
                                </Group>
                              </Paper>
                            ))
                        )}
                      </Stack>
                    </SectionCard>
                  </SimpleGrid>

                  <SectionCard
                    title="Aksiyonlar"
                    description="Teslim alma sonrası sonraki akışlar için demo butonları."
                    action={<Button variant="default" leftSection={<IconSearch size={16} />}>Tutanak Ön İzleme</Button>}
                  >
                    <SimpleGrid cols={{ base: 1, md: 4 }} spacing="sm">
                      <Button color="teal" leftSection={<IconShieldCheck size={16} />}>Mal Kabulü Kaydet</Button>
                      <Button variant="light" color="orange" leftSection={<IconRotate size={16} />}>İade Sürecini Başlat</Button>
                      <Button variant="light" color="blue" leftSection={<IconReceipt2 size={16} />}>Fatura Aşamasına Geç</Button>
                      <Button variant="light" color="gray" leftSection={<IconPackageOff size={16} />}>Kısmi Teslim Olarak İşaretle</Button>
                    </SimpleGrid>
                  </SectionCard>
                </>
              )}
            </Stack>
          </Grid.Col>
        </Grid>

        <Paper withBorder radius="lg" p="md" shadow="sm" style={{ borderStyle: 'dashed' }}>
          <Group align="flex-start" wrap="nowrap">
            <ThemeIcon color="blue" variant="light" mt={2}><IconInfoCircle size={16} /></ThemeIcon>
            <Text size="sm" c="dimmed">
              Sonraki mantıklı ekran, bu mal kabulden türeyen “İade Yönetimi” ekranı olur. Ardından fatura kayıt ekranına geçerek
              satın alma sürecinin operasyonel zincirini tamamlayabiliriz.
            </Text>
          </Group>
        </Paper>
      </Stack>
    </Container>
  );
}
